/**
 * ResumeMetric - LinkedIn Profile Optimizer Engine
 * 100% Client-Side LinkedIn Profile Analysis, SEO Keyword Scoring, & Truncation Auditor
 * @module tools/linkedinOptimizer
 */

export const ACTION_VERBS = [
    'spearheaded', 'engineered', 'architected', 'accelerated', 'optimized', 'developed',
    'scaled', 'pioneered', 'transformed', 'orchestrated', 'streamlined', 'generated',
    'increased', 'launched', 'delivered', 'formulated', 'championed', 'revamped',
    'designed', 'expanded', 'built', 'executed', 'implemented', 'maximized', 'driven'
];

export const BUZZWORDS = [
    { word: 'guru', suggestion: 'Expert / Specialist' },
    { word: 'ninja', suggestion: 'Specialist / Lead' },
    { word: 'rockstar', suggestion: 'High Performer / Lead' },
    { word: 'synergy', suggestion: 'Cross-functional Collaboration' },
    { word: 'thought leader', suggestion: 'Subject Matter Expert' },
    { word: 'out-of-the-box', suggestion: 'Innovative / Creative' },
    { word: 'hardworking', suggestion: 'Results-driven / Dedicated' },
    { word: 'detail-oriented', suggestion: 'Analytical / Meticulous' },
    { word: 'passionate', suggestion: 'Dedicated / Focused' },
    { word: 'self-starter', suggestion: 'Proactive / Autonomous' },
    { word: 'dynamic', suggestion: 'Adaptable / Versatile' },
    { word: 'motivated', suggestion: 'Goal-oriented' },
    { word: 'team player', suggestion: 'Collaborative Leader' }
];

export const WEAK_PHRASES = [
    { phrase: 'responsible for', suggestion: 'spearheaded / managed' },
    { phrase: 'duties included', suggestion: 'executed / directed' },
    { phrase: 'helped with', suggestion: 'collaborated on / accelerated' },
    { phrase: 'assisted in', suggestion: 'contributed to / facilitated' },
    { phrase: 'worked on', suggestion: 'engineered / delivered' },
    { phrase: 'handled', suggestion: 'orchestrated / led' }
];

import KEYWORDS from './keywords.json';

export function analyzeHeadline(headline = '') {
    const trimmed = headline.trim();
    const len = trimmed.length;
    const words = trimmed ? trimmed.split(/\s+/).length : 0;
    const max = 220;
    const desktopTruncate = 120;
    const mobileTruncate = 75;
    const isOver = len > max;

    const desktopPreview = len > desktopTruncate ? trimmed.substring(0, desktopTruncate) + '...' : trimmed;
    const mobilePreview = len > mobileTruncate ? trimmed.substring(0, mobileTruncate) + '...' : trimmed;

    const hasRoleInFront = /^[A-Za-z0-9\s-]{3,40}(?=[|•\-/]|$)/i.test(trimmed);
    const hasDivider = /[|•\-//]/.test(trimmed);
    const hasValueProp = len > 40;
    const hasMetric = /\b\d+(%|\+|\$|k|m|x)?\b/i.test(trimmed);
    const detectedBuzzwords = BUZZWORDS.filter(b => trimmed.toLowerCase().includes(b.word));

    let score = 0;
    if (len > 0) score += 15;
    if (len >= 60 && len <= 200) score += 25;
    if (hasDivider) score += 15;
    if (hasRoleInFront) score += 20;
    if (hasMetric) score += 15;
    if (detectedBuzzwords.length === 0 && len > 0) score += 10;

    const feedback = [];
    if (len === 0) {
        feedback.push('Add your headline to start optimization.');
    } else {
        if (!hasRoleInFront) feedback.push('Put your target job title in the first 40 characters for top recruiter search ranking.');
        if (len < 60) feedback.push('Headline is short. Add target keywords, tech stack, or quantified achievements.');
        if (len > desktopTruncate) feedback.push(`Desktop search results truncate at ~${desktopTruncate} chars. Keep vital titles near the front.`);
        if (len > mobileTruncate) feedback.push(`Mobile app search truncates at ~${mobileTruncate} chars: "${mobilePreview}".`);
        if (!hasDivider) feedback.push('Use clear separators like "|" or "•" to divide your Title, Skills, and Impact.');
        if (!hasMetric) feedback.push('Incorporate numbers or metrics in your headline (e.g. "2M+ Users", "$45k Saved", "8+ Yrs Exp").');
        if (detectedBuzzwords.length > 0) {
            feedback.push(`Found cliché buzzwords: ${detectedBuzzwords.map(b => `"${b.word}" → replace with ${b.suggestion}`).join(', ')}.`);
        }
    }

    return {
        length: len,
        words,
        max,
        desktopTruncate,
        mobileTruncate,
        isOver,
        desktopPreview,
        mobilePreview,
        score: Math.min(score, 100),
        hasDivider,
        hasRoleInFront,
        hasMetric,
        detectedBuzzwords,
        feedback
    };
}

export function analyzeAbout(about = '') {
    const trimmed = about.trim();
    const len = trimmed.length;
    const max = 2600;
    const searchTruncate = 300;
    const isOver = len > max;
    const truncatedPreview = len > searchTruncate ? trimmed.substring(0, searchTruncate) + '...' : trimmed;

    const words = trimmed ? trimmed.split(/\s+/).length : 0;
    const paragraphs = trimmed.split(/\n\s*\n/).filter(p => p.trim().length > 0).length;
    const hasBullets = /[•▶⚡▪✓🔹\-*]\s+/.test(trimmed);
    const hasContact = /(email|contact|reach out|mail|@|linkedin|github|portfolio|calendly)/i.test(trimmed);
    const hasCallToAction = /(let's connect|contact me|reach out|email me|open to|connect with me)/i.test(trimmed);
    const hasMetrics = /\b\d+(%|\+|\$|k|m|x)?\b/i.test(trimmed);
    const detectedActionVerbs = ACTION_VERBS.filter(verb => new RegExp(`\\b${verb}\\b`, 'i').test(trimmed));
    const detectedBuzzwords = BUZZWORDS.filter(b => trimmed.toLowerCase().includes(b.word));

    let score = 0;
    if (len > 0) score += 15;
    if (len >= 300 && len <= 2200) score += 25;
    if (hasBullets) score += 15;
    if (paragraphs >= 2) score += 15;
    if (hasContact || hasCallToAction) score += 15;
    if (hasMetrics) score += 15;

    const feedback = [];
    if (len === 0) {
        feedback.push('Paste your "About / Summary" section for a full audit.');
    } else {
        if (len < 300) feedback.push('About section is too brief. Aim for 300–1,500 characters to craft your narrative.');
        if (paragraphs < 2) feedback.push('Break long text into 2–4 short paragraphs with whitespace for mobile scannability.');
        if (!hasBullets) feedback.push('Use bullet symbols (e.g. •, ▶, ⚡) to highlight key achievements.');
        if (!hasCallToAction && !hasContact) feedback.push('Add a clear Call-To-Action or contact email at the end (e.g., "Open to roles | Email: alex@example.com").');
        if (!hasMetrics) feedback.push('Add quantified metrics (e.g., "grew traffic by 40%", "managed $2M budget").');
        if (detectedActionVerbs.length < 2) feedback.push('Incorporate high-impact action verbs (e.g., Spearheaded, Scaled, Transformed).');
        if (detectedBuzzwords.length > 0) feedback.push(`Avoid clichés: ${detectedBuzzwords.map(b => `"${b.word}" → ${b.suggestion}`).join('; ')}.`);
    }

    return {
        length: len,
        words,
        paragraphs,
        max,
        searchTruncate,
        isOver,
        truncatedPreview,
        score: Math.min(score, 100),
        hasContact,
        hasCallToAction,
        hasMetrics,
        hasBullets,
        actionVerbCount: detectedActionVerbs.length,
        detectedBuzzwords,
        feedback
    };
}

export function analyzeExperience(expInput = '') {
    let textToAnalyze = '';
    let jobCount = 0;

    if (Array.isArray(expInput)) {
        jobCount = expInput.filter(j => j.title || j.description).length;
        textToAnalyze = expInput.map(j => `${j.title || ''} ${j.company ? 'at ' + j.company : ''}\n${j.description || ''}`).join('\n\n');
    } else {
        textToAnalyze = (expInput || '').trim();
        const jobEntries = textToAnalyze.split(/\n\s*\n/).filter(p => p.trim().length > 20);
        jobCount = Math.max(jobEntries.length, textToAnalyze ? 1 : 0);
    }

    const trimmed = textToAnalyze.trim();
    const words = trimmed ? trimmed.split(/\s+/).length : 0;

    if (!trimmed || jobCount === 0) {
        return {
            words: 0,
            jobCount: 0,
            bulletCount: 0,
            metricsCount: 0,
            score: 0,
            weakPhrases: [],
            feedback: ['Add your recent work experience entries (Title, Company, Achievements) for a full recruiter audit.']
        };
    }

    const bullets = (trimmed.match(/^[•▶⚡▪✓🔹\-*]\s+.+/gm) || []).length;
    const metrics = (trimmed.match(/\b\d+(%|\+|\$|k|m|x)?\b/gi) || []).length;
    const weakPhrases = WEAK_PHRASES.filter(w => new RegExp(`\\b${w.phrase}\\b`, 'i').test(trimmed));

    let score = 30;
    if (jobCount >= 2) score += 20;
    if (bullets >= jobCount * 2 || bullets >= 3) score += 25;
    if (metrics >= jobCount) score += 25;
    if (weakPhrases.length === 0) score += 10;

    const feedback = [];
    if (jobCount < 2) feedback.push('Add at least 2 recent job entries to establish your career trajectory.');
    if (bullets < jobCount * 2) feedback.push('Aim for 3–5 bullet points per job role using symbols (e.g. •, ▶).');
    if (metrics < jobCount) feedback.push('Quantify experience! Add at least one metric (%, $, team size) per position.');
    if (weakPhrases.length > 0) feedback.push(`Replace weak phrases: ${weakPhrases.map(w => `"${w.phrase}" → use "${w.suggestion}"`).join('; ')}.`);

    return {
        words,
        jobCount,
        bulletCount: bullets,
        metricsCount: metrics,
        score: Math.min(score, 100),
        weakPhrases,
        feedback: feedback.length > 0 ? feedback : ['✓ Excellent accomplishment-driven experience section!']
    };
}

export function analyzeSkills(skillsText = '', industry = 'tech', country = '') {
    const rawSkills = skillsText.split(/[,;\n]/).map(s => s.trim()).filter(s => s.length > 0);
    const count = rawSkills.length;
    const max = 50;
    const isOver = count > max;

    const industryTerms = KEYWORDS.industryKeywords[industry] || KEYWORDS.industryKeywords.tech;
    const countryTerms = (country && KEYWORDS.countryKeywords[country] && KEYWORDS.countryKeywords[country][industry]) ? KEYWORDS.countryKeywords[country][industry] : [];
    const combinedTerms = Array.from(new Set([...industryTerms, ...countryTerms]));
    const matchedIndustrySkills = rawSkills.filter(s => combinedTerms.some(it => it.toLowerCase() === s.toLowerCase()));

    let score = 0;
    if (count >= 5) score += 40;
    if (count >= 15 && count <= 50) score += 30;
    if (matchedIndustrySkills.length >= 3) score += 30;

    const feedback = [];
    if (count === 0) {
        feedback.push('Add your skills list (comma-separated). LinkedIn allows up to 50 skills.');
    } else {
        if (count < 5) feedback.push('LinkedIn requires at least 5 skills to reach All-Star status (aim for 15–30).');
        if (isOver) feedback.push(`You have listed ${count} skills. LinkedIn caps skills at 50.`);
        if (matchedIndustrySkills.length < 3) feedback.push(`Include core ${industry.toUpperCase()} skills like: ${industryTerms.slice(0, 5).join(', ')}.`);
    }

    return {
        count,
        max,
        isOver,
        rawSkills,
        matchedIndustrySkills,
        score: Math.min(score, 100),
        feedback: feedback.length > 0 ? feedback : ['✓ Excellent skill set targeting your industry!']
    };
}

export function calculateKeywordHeatmap(headline = '', about = '', experience = '', skills = '', industry = 'tech') {
    const keywords = KEYWORDS.industryKeywords[industry] || KEYWORDS.industryKeywords.tech;
    const hLower = headline.toLowerCase();
    const aLower = about.toLowerCase();
    const eLower = experience.toLowerCase();
    const sLower = skills.toLowerCase();

    return keywords.map(kw => {
        const kwL = kw.toLowerCase();
        const inHeadline = hLower.includes(kwL);
        const inAbout = aLower.includes(kwL);
        const inExp = eLower.includes(kwL);
        const inSkills = sLower.includes(kwL);

        let location = 'missing';
        if (inHeadline) location = 'headline';
        else if (inAbout) location = 'about';
        else if (inExp || inSkills) location = 'exp_skills';

        return {
            keyword: kw,
            inHeadline,
            inAbout,
            inExp,
            inSkills,
            location
        };
    });
}

export function validateCustomUrl(urlInput = '') {
    let raw = urlInput.trim();
    // Robust handle extractor stripping protocol, domain, and /in/ path
    let clean = raw.replace(/^(https?:\/\/)?(www\.)?linkedin\.com\/in\//i, '');
    clean = clean.replace(/\/$/, '').trim();

    if (!clean) return { url: '', isValid: false, score: 0, feedback: ['Enter your custom LinkedIn URL (e.g. linkedin.com/in/alexmorgan)'] };

    const hasNumbers = /\d{4,}/.test(clean);
    const hasSpecialChars = /[^a-zA-Z0-9-]/.test(clean);
    const hasUppercase = /[A-Z]/.test(clean);

    let score = 100;
    const feedback = [];

    if (hasSpecialChars) {
        score -= 40;
        feedback.push('URL contains invalid characters. Use letters, numbers, and hyphens only.');
    }
    if (hasNumbers) {
        score -= 30;
        feedback.push('URL contains random numbers (e.g. -8914812). Remove numbers for a cleaner personal brand.');
    }
    if (hasUppercase) {
        score -= 10;
        feedback.push('Use lowercase letters for consistent link formatting.');
    }

    const handleOnly = clean.split('/')[0].split('?')[0];
    const suggested = handleOnly.toLowerCase().replace(/[^a-z0-9-]/g, '').replace(/-\d+$/, '');

    return {
        url: clean,
        isValid: score >= 70,
        score: Math.max(score, 0),
        suggestedUrl: `linkedin.com/in/${suggested || 'yourname'}`,
        feedback: feedback.length > 0 ? feedback : ['✓ Clean, professional custom LinkedIn URL!']
    };
}

export function extractKeywordMatch(text = '', industry = 'tech', country = '') {
    // Combine industry keywords with optional country-specific keywords
    const industryTerms = KEYWORDS.industryKeywords[industry] || KEYWORDS.industryKeywords.tech;
    const countryTerms = (country && KEYWORDS.countryKeywords[country] && KEYWORDS.countryKeywords[country][industry]) ? KEYWORDS.countryKeywords[country][industry] : [];
    const keywords = Array.from(new Set([...industryTerms, ...countryTerms]));

    const lowerText = text.toLowerCase();
    const matched = [];
    const missing = [];

    keywords.forEach(kw => {
        if (lowerText.includes(kw.toLowerCase())) {
            matched.push(kw);
        } else {
            missing.push(kw);
        }
    });

    const matchPercentage = keywords.length ? Math.round((matched.length / keywords.length) * 100) : 0;

    return {
        industry,
        country,
        matched,
        missing,
        matchPercentage
    };
}


export function generateHeadlineVariants(role = 'Software Engineer', skills = 'React, Node.js, AWS', metric = 'Scaling 2M+ User Apps') {
    const cleanRole = role.trim() || 'Software Leader';
    const cleanSkills = skills.trim() || 'Core Tech & Architecture';
    const cleanMetric = metric.trim() || 'Driving 40% Growth';

    return [
        `${cleanRole} | ${cleanSkills} | ${cleanMetric}`,
        `${cleanRole} • Specializing in ${cleanSkills.split(',')[0] || 'High-Impact Systems'} • ${cleanMetric}`,
        `Senior ${cleanRole} | ${cleanSkills} | Open to Lead Roles`
    ];
}

export function calculateOverallScore(headlineResult, aboutResult, experienceResult, skillsResult, keywordResult, urlResult = null) {
    const hScore = headlineResult ? headlineResult.score : 0;
    const aScore = aboutResult ? aboutResult.score : 0;
    const eScore = experienceResult ? experienceResult.score : 0;
    const sScore = skillsResult ? skillsResult.score : (keywordResult ? keywordResult.matchPercentage : 0);

    const overall = Math.round((hScore * 0.25) + (aScore * 0.25) + (eScore * 0.25) + (sScore * 0.25));
    return Math.min(overall, 100);
}
